/**
 * MAYBOURNE PRODUCT MODALS
 * 
 * Handles interactions for all Maybourne-specific product modals:
 * - Age Check Modal
 * - Preorder Modal  
 * - Back in Stock Modal
 * - Size Guide Modal
 * - Allergens Modal
 */

class MaybourneProductModals {
  constructor(container = document) {
    this.container = container;
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Handle buy button clicks to check for modal requirements
    this.container.addEventListener('click', (event) => {
      // Try multiple selectors to find the buy button
      let buyButton = event.target.closest('button[type="submit"][form]');
      
      // If no form attribute, check if it's inside a form or is a product submit button
      if (!buyButton) {
        const submitButton = event.target.closest('button[type="submit"]');
        if (submitButton) {
          // Check if it's inside a product form or has product-related classes
          const productForm = submitButton.closest('form[action*="/cart/add"]') || 
                             submitButton.closest('.product-form') ||
                             submitButton.closest('product-form');
          
          if (productForm || submitButton.textContent.toLowerCase().includes('add to cart')) {
            buyButton = submitButton;
          }
        }
      }
      
      if (buyButton) {
        const shouldIntercept = this.shouldInterceptSubmit(buyButton);
        
        if (shouldIntercept) {
          event.preventDefault();
          this.handleBuyButtonClick(buyButton);
        }
      }
    });

    // Handle back in stock button clicks
    this.container.addEventListener('click', (event) => {
      const bisButton = event.target.closest('.maybourne-back-in-stock-btn');
      if (bisButton) {
        this.handleBackInStockButtonClick(bisButton);
      }
    });

    // Handle preorder button clicks
    this.container.addEventListener('click', (event) => {
      const preorderButton = event.target.closest('.maybourne-preorder-btn');
      if (preorderButton) {
        this.handlePreorderButtonClick(preorderButton);
      }
    });
  }

  /**
   * Destroy the component and clean up event listeners
   */
  destroy() {
    // Clean up any stored callbacks
    if (window.maybourneAgeCheckCallback) {
      window.maybourneAgeCheckCallback = null;
    }
    if (window.maybournePreorderCallback) {
      window.maybournePreorderCallback = null;
    }
  }

  shouldInterceptSubmit(button) {
    // Get form either by form attribute or by closest form
    const formId = button.getAttribute('form');
    let form = null;
    
    if (formId) {
      form = document.getElementById(formId);
    } else {
      form = button.closest('form');
    }
    
    if (!form) {
      return false;
    }

    const productData = this.getProductDataFromForm(form);
    
    // Check if age verification is needed and not already confirmed
    const needsAgeCheck = productData.needsAgeCheck && !this.isAgeVerified();
    
    return needsAgeCheck || productData.isPreorder;
  }

  getProductDataFromForm(form) {
    // Extract product data from form or surrounding elements
    const productContainer = form.closest('[data-product-id]') || 
                           form.closest('.product-info') ||
                           this.container.querySelector('.product-info');
    
    return {
      needsAgeCheck: this.container.querySelector('.maybourne-age-check-modal') !== null,
      isPreorder: this.container.querySelector('.maybourne-preorder-modal') !== null,
      hasBackInStock: this.container.querySelector('.maybourne-bis-modal') !== null
    };
  }

    handleBuyButtonClick(button) {
    // Get form either by form attribute or by closest form
    const formId = button.getAttribute('form');
    let form = null;
    
    if (formId) {
      form = document.getElementById(formId);
    } else {
      form = button.closest('form');
    }
    
    if (!form) {
      return;
    }
    
    const productData = this.getProductDataFromForm(form);
    const actualFormId = form.getAttribute('id');

    // Check if age verification is needed and not already confirmed
    const needsAgeCheck = productData.needsAgeCheck && !this.isAgeVerified();

    if (needsAgeCheck && productData.isPreorder) {
      // Show age check first, then preorder
      this.showAgeCheckModal(actualFormId, () => {
        this.showPreorderModal(actualFormId, () => {
          this.submitForm(form);
        });
      });
    } else if (needsAgeCheck) {
      this.showAgeCheckModal(actualFormId, () => {
        this.submitForm(form);
      });
    } else if (productData.isPreorder) {
      this.showPreorderModal(actualFormId, () => {
        this.submitForm(form);
      });
    } else {
      this.submitForm(form);
    }
  }

  handleBackInStockButtonClick(button) {
    // Extract modal ID from the onclick attribute or find the form
    const form = button.closest('form') || 
                 this.container.getElementById(button.getAttribute('form')) ||
                 button.closest('.buy-buttons').querySelector('form');
    
    if (form) {
      const bisModalId = form.getAttribute('id') + '-back-in-stock';
      this.openModal(bisModalId);
    }
  }

  handlePreorderButtonClick(button) {
    // Find the form associated with this preorder button
    const form = button.closest('form') || 
                 this.container.getElementById(button.getAttribute('form')) ||
                 button.closest('.buy-buttons').querySelector('form');
    
    if (!form) {
      return;
    }
    
    const productData = this.getProductDataFromForm(form);
    const actualFormId = form.getAttribute('id');

    // Check if age verification is needed and not already confirmed
    const needsAgeCheck = productData.needsAgeCheck && !this.isAgeVerified();

    if (needsAgeCheck) {
      // Show age check first, then preorder
      this.showAgeCheckModal(actualFormId, () => {
        this.showPreorderModal(actualFormId, () => {
          this.submitForm(form);
        });
      });
    } else {
      // Just show preorder modal
      this.showPreorderModal(actualFormId, () => {
        this.submitForm(form);
      });
    }
  }

  showAgeCheckModal(formId, onConfirm) {
    const modalId = formId + '-age-check';
    this.openModal(modalId);
    
    // Store callback for age check confirmation
    window.maybourneAgeCheckCallback = onConfirm;
  }

  showPreorderModal(formId, onConfirm) {
    const modalId = formId + '-preorder';
    this.openModal(modalId);
    
    // Store callback for preorder confirmation
    window.maybournePreorderCallback = onConfirm;
  }

  openModal(modalId) {
    const modal = this.container.getElementById ? this.container.getElementById(modalId) : document.getElementById(modalId);
    if (modal && modal.show) {
      modal.show();
    }
  }

  closeModal(modalId) {
    const modal = this.container.getElementById ? this.container.getElementById(modalId) : document.getElementById(modalId);
    if (modal && modal.hide) {
      modal.hide();
    }
  }

  async submitForm(form) {
    if (!form) return;
    
    // Use AJAX submission like the theme's normal cart functionality
    try {
      // Set loading state
      const submitButtons = Array.from(form.elements).filter(button => button.type === 'submit');
      submitButtons.forEach(button => {
        button.setAttribute('aria-busy', 'true');
      });
      
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      
      // Prepare sections to bundle (for cart drawer refresh)
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { 
        bubbles: true, 
        detail: { sections: sectionsToBundle } 
      }));
      
      const formData = new FormData(form);
      formData.set('sections', sectionsToBundle.join(','));
      formData.set('sections_url', `${Shopify.routes.root}variants/${form.id.value}`);
      
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      // Remove loading state
      submitButtons.forEach(button => {
        button.removeAttribute('aria-busy');
      });
      
      const responseJson = await response.json();
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      
      if (response.ok) {
        // Don't redirect to cart page if it's a drawer setup
        if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
          return window.location.href = `${Shopify.routes.root}cart`;
        }
        
        // Get updated cart content
        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent['sections'] = responseJson['sections'];
        
        // Dispatch events to trigger cart drawer opening
        form.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
            cart: cartContent,
            onSuccessDo: formData.get('on_success')
          }
        }));
        
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            onSuccessDo: formData.get('on_success'),
            cart: cartContent
          }
        }));
      } else {
        // Handle errors
        form.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: {
            error: responseJson['description']
          }
        }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Fallback to regular form submission
      if (form && form.submit) {
        form.submit();
      }
    }
  }

  // Back in Stock form submission
  async submitBackInStockForm(formData, modalId) {
    try {
      // Here you would integrate with your back in stock service
      // For now, we'll just show a success message
      console.log('Back in stock submission:', formData);
      
      // Close modal and show success
      this.closeModal(modalId);
      this.showNotification('Thank you! We\'ll notify you when this item is back in stock.', 'success');
      
      // You could integrate with Klaviyo, Shopify Flow, or other services here
      
    } catch (error) {
      console.error('Back in stock submission failed:', error);
      this.showNotification('Sorry, there was an error. Please try again.', 'error');
    }
  }

  showNotification(message, type = 'info') {
    // Create a simple notification, using banner styling
    const notification = document.createElement('div');
    notification.className = `maybourne-notification banner banner--${type}`;
    notification.textContent = message;

    document.body.appendChild(notification);

    // Remove after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 5000);
  }

  // Age verification local storage methods
  isAgeVerified() {
    try {
      const ageVerification = localStorage.getItem('maybourne-age-verified');
      if (!ageVerification) return false;
      
      const verification = JSON.parse(ageVerification);
      const now = new Date().getTime();
      const verificationTime = verification.timestamp;
      
      // Age verification expires after 24 hours (86400000 ms)
      const expirationTime = 24 * 60 * 60 * 1000;
      
      return (now - verificationTime) < expirationTime;
    } catch (error) {
      // If there's any error reading from localStorage, assume not verified
      return false;
    }
  }

  setAgeVerified() {
    try {
      const verification = {
        timestamp: new Date().getTime(),
        verified: true
      };
      localStorage.setItem('maybourne-age-verified', JSON.stringify(verification));
    } catch (error) {
      // Silently fail if localStorage is not available (e.g., private browsing)
      console.warn('Could not save age verification to localStorage:', error);
    }
  }
}

// Global functions called by modal buttons
// These need to be global because they're called from inline onclick handlers
window.maybourneAgeCheckConfirm = function(modalId, formId) {
  const modals = window.maybourneApp?.componentManager?.instances?.productModals;
  if (modals) {
    // Save age verification to localStorage
    modals.setAgeVerified();
    
    modals.closeModal(modalId);
    
    if (window.maybourneAgeCheckCallback) {
      window.maybourneAgeCheckCallback();
      window.maybourneAgeCheckCallback = null;
    }
  }
};

window.maybourneAgeCheckDecline = function(modalId) {
  const modals = window.maybourneApp?.componentManager?.instances?.productModals;
  if (modals) {
    modals.closeModal(modalId);
    modals.showNotification('You must be 18 or older to purchase this product.', 'error');
  }
};

window.maybournePreorderConfirm = function(modalId, formId) {
  const modals = window.maybourneApp?.componentManager?.instances?.productModals;
  if (modals) {
    modals.closeModal(modalId);
    
    if (window.maybournePreorderCallback) {
      window.maybournePreorderCallback();
      window.maybournePreorderCallback = null;
    }
  }
};

window.maybournePreorderCancel = function(modalId) {
  const modals = window.maybourneApp?.componentManager?.instances?.productModals;
  if (modals) {
    modals.closeModal(modalId);
  }
};

window.maybourneBackInStockSubmit = function(event, modalId) {
  event.preventDefault();
  
  const form = event.target;
  const formData = new FormData(form);
  const data = {
    email: formData.get('email'),
    marketing: formData.get('marketing') === 'on',
    product: window.location.pathname // Simple product identification
  };
  
  const modals = window.maybourneApp?.componentManager?.instances?.productModals;
  if (modals) {
    modals.submitBackInStockForm(data, modalId);
  }
};

window.maybourneBackInStockCancel = function(modalId) {
  const modals = window.maybourneApp?.componentManager?.instances?.productModals;
  if (modals) {
    modals.closeModal(modalId);
  }
};



export default MaybourneProductModals; 